const clean = (v) => (v === null || v === undefined ? '' : String(v).trim());
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const pad2 = (n) => String(n).padStart(2, '0');

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ── Dates ───────────────────────────────────────────────────────────────── */

/** The APIs mix ISO and .NET ("8/21/2026 12:00:00 AM") date strings. */
export function parseDate(value) {
  const s = clean(value);
  if (!s) return null;

  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);

  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (m) {
    const [, a, b, y] = m;
    // Above 12 in the first slot can only be a day.
    return Number(a) > 12 ? new Date(+y, +b - 1, +a) : new Date(+y, +a - 1, +b);
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const isoDate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Inclusive list of ISO days between two dates. */
export function eachDay(from, to) {
  const out = [];
  const cur = startOfDay(from);
  const end = startOfDay(to);
  while (cur <= end) {
    out.push(isoDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** First and last day of the month containing `today`, as ISO strings. */
export function monthBounds(today = new Date()) {
  const from = new Date(today.getFullYear(), today.getMonth(), 1);
  const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return { fromDate: isoDate(from), toDate: isoDate(to) };
}

/* ── Money / numbers ─────────────────────────────────────────────────────── */

/** "Rs. 2.85M" — compact enough for a KPI tile. */
export function fmtCompactRs(n) {
  const v = num(n);
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `Rs. ${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `Rs. ${(v / 1_000).toFixed(1)}K`;
  return `Rs. ${v.toFixed(0)}`;
}

export const fmtRs = (n) =>
  `Rs. ${num(n).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtHours = (n) => `${Math.round(num(n) * 10) / 10} hrs`;

export const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

/* ── Employees ───────────────────────────────────────────────────────────── */

const empActive = (e) => {
  if (e.HED_IS_ACTIVE !== undefined && e.HED_IS_ACTIVE !== null) {
    return e.HED_IS_ACTIVE === true || clean(e.HED_IS_ACTIVE).toLowerCase() === 'true';
  }
  // Older rows only carry the inactive-reason column.
  const t = clean(e.p_inactive_type);
  return t === '' || t === '0';
};

/** "True"/"1" only — anything else, including null, is still pending. */
const empApproved = (e) => {
  const raw = e.HED_IS_APPROVED;
  if (raw === true) return true;
  if (raw === false || raw === null || raw === undefined) return false;
  const s = clean(raw).toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
};

export const employeeServiceNo = (e) =>
  clean(e.HED_SERVICE_NO) || clean(e.p_ced_service_no);

export const employeeName = (e) =>
  clean(e.HED_REPORT_NAME) ||
  [clean(e.HED_FIRST_NAME), clean(e.HED_LAST_NAME)].filter(Boolean).join(' ') ||
  clean(e.p_report_name) ||
  employeeServiceNo(e) ||
  'Unknown';

export function computeHeadcount(employees = []) {
  const active = employees.filter(empActive);
  const pendingApproval = employees.filter((e) => !empApproved(e));

  return {
    total: employees.length,
    active: active.length,
    inactive: employees.length - active.length,
    pendingApproval: pendingApproval.length,
    activeRate: pct(active.length, employees.length),
    departments: new Set(
      employees.map((e) => clean(e.HDD_DEPARTMENT_NAME)).filter(Boolean)
    ).size,
  };
}

/** Bank accounts still waiting on approval, across every employee. */
export function countPendingBankAccounts(employees = []) {
  let n = 0;
  employees.forEach((e) => {
    (e.Banks || []).forEach((b) => {
      const raw = b.HEB_IS_APPROVED;
      const s = clean(raw).toLowerCase();
      const approved = raw === true || s === 'true' || s === '1' || s === 'yes';
      if (!approved) n += 1;
    });
  });
  return n;
}

function countsByField(rows, field, limit = 0) {
  const buckets = {};
  rows.forEach((r) => {
    const key = clean(r[field]) || 'Unassigned';
    buckets[key] = (buckets[key] || 0) + 1;
  });
  const out = Object.entries(buckets)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
  return limit ? out.slice(0, limit) : out;
}

export const buildHeadcountByDepartment = (employees = [], limit = 8) =>
  countsByField(employees.filter(empActive), 'HDD_DEPARTMENT_NAME', limit);

export const buildHeadcountByCategory = (employees = []) =>
  countsByField(employees.filter(empActive), 'HEC_CATEGORY_NAME');

/** New hires per month over the last `months` months, oldest first. */
export function buildHiringTrend(employees = [], months = 12, today = new Date()) {
  const buckets = [];
  const index = {};
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
    const row = { key, label: `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`, hires: 0 };
    index[key] = row;
    buckets.push(row);
  }
  employees.forEach((e) => {
    const d = parseDate(e.HED_DATE_OF_JOINING) || parseDate(e.p_recruitment_date);
    if (!d) return;
    const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
    if (index[key]) index[key].hires += 1;
  });
  return buckets;
}

/* ── Attendance ──────────────────────────────────────────────────────────── */
/* Rows from Attendance/GetEmployeesAttendance are PascalCase. */

const attDateKey = (r) => {
  const d = parseDate(r.AttDate);
  return d ? isoDate(d) : '';
};
const hasPunch = (r) => !!clean(r.InTime);

/** Distinct employees with an in-punch on the given ISO day. */
export function presentOn(rows = [], isoDay) {
  const who = new Set();
  rows.forEach((r) => {
    if (attDateKey(r) === isoDay && hasPunch(r)) {
      who.add(clean(r.ServiceNo) || clean(r.EmployeeID));
    }
  });
  who.delete('');
  return who.size;
}

/** Present-count per day across the range, for the trend chart. */
export function buildAttendanceTrend(rows = [], fromDate, toDate) {
  const from = parseDate(fromDate);
  const to = parseDate(toDate);
  if (!from || !to) return [];

  const byDay = {};
  rows.forEach((r) => {
    const key = attDateKey(r);
    if (!key || !hasPunch(r)) return;
    if (!byDay[key]) byDay[key] = new Set();
    byDay[key].add(clean(r.ServiceNo) || clean(r.EmployeeID));
  });

  return eachDay(from, to).map((day) => {
    const d = parseDate(day);
    return {
      day,
      label: `${d.getDate()}`,
      present: byDay[day] ? byDay[day].size : 0,
      weekend: d.getDay() === 0 || d.getDay() === 6,
    };
  });
}

/** Working days so far this month (days that saw at least one punch). */
export function attendanceKpis(rows = [], activeHeadcount = 0, today = new Date()) {
  const todayIso = isoDate(startOfDay(today));
  const trend = {};
  rows.forEach((r) => {
    const key = attDateKey(r);
    if (!key || !hasPunch(r)) return;
    if (!trend[key]) trend[key] = new Set();
    trend[key].add(clean(r.ServiceNo) || clean(r.EmployeeID));
  });

  const days = Object.keys(trend);
  const totalPresence = days.reduce((s, d) => s + trend[d].size, 0);

  return {
    presentToday: trend[todayIso] ? trend[todayIso].size : 0,
    workedDays: days.length,
    avgDailyPresence: days.length ? totalPresence / days.length : 0,
    // Averaged over the days that actually had attendance, so a month that is
    // only half over doesn't read as 50% attendance.
    attendanceRate: days.length && activeHeadcount
      ? pct(totalPresence / days.length, activeHeadcount)
      : 0,
  };
}

/* ── Leave ───────────────────────────────────────────────────────────────── */

export const LEAVE_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'];

export const STATUS_COLORS = {
  PENDING: '#d97706',
  APPROVED: '#059669',
  REJECTED: '#dc2626',
  CANCELLED: '#94a3b8',
};

const leaveDays = (r) => num(r.HLF_TOTAL_DAYS);

export function leaveKpis(byStatus = {}, today = new Date()) {
  const rowsFor = (s) => byStatus[s] || [];
  const approved = rowsFor('APPROVED');
  const t = startOfDay(today).getTime();

  const onLeaveToday = new Set();
  approved.forEach((r) => {
    const from = parseDate(r.HLF_FROM_DATE);
    const to = parseDate(r.HLF_TO_DATE) || from;
    if (!from) return;
    if (startOfDay(from).getTime() <= t && t <= startOfDay(to).getTime()) {
      onLeaveToday.add(clean(r.HLF_EMP_SERVICE_NO));
    }
  });

  return {
    pending: rowsFor('PENDING').length,
    approved: approved.length,
    rejected: rowsFor('REJECTED').length,
    pendingDays: rowsFor('PENDING').reduce((s, r) => s + leaveDays(r), 0),
    onLeaveToday: onLeaveToday.size,
  };
}

export function buildLeaveStatusDonut(byStatus = {}) {
  return LEAVE_STATUSES
    .map((s) => ({
      name: s.charAt(0) + s.slice(1).toLowerCase(),
      value: (byStatus[s] || []).length,
      color: STATUS_COLORS[s],
    }))
    .filter((d) => d.value > 0);
}

/** id → name, for joining requests to their leave type. */
export function leaveTypeMap(types = []) {
  const map = {};
  types.forEach((t) => {
    map[clean(t.HLT_TYPE_ID)] = clean(t.HLT_NAME) || clean(t.HLT_CODE) || 'Unknown';
  });
  return map;
}

/** The pending queue, soonest start first — what actually needs a decision. */
export function buildLeaveQueue(byStatus = {}, typeMap = {}, nameByServiceNo = {}, limit = 5) {
  return (byStatus.PENDING || [])
    .map((r) => {
      const sno = clean(r.HLF_EMP_SERVICE_NO);
      return {
        formNo: clean(r.HLF_FORM_NO),
        serviceNo: sno,
        name: nameByServiceNo[sno] || sno || '—',
        type: typeMap[clean(r.HLF_LEAVE_TYPE_ID)] || '—',
        from: r.HLF_FROM_DATE,
        days: leaveDays(r),
        _sort: (parseDate(r.HLF_FROM_DATE) || new Date(8640000000000000)).getTime(),
      };
    })
    .sort((a, b) => a._sort - b._sort)
    .slice(0, limit);
}

/* ── Holidays ────────────────────────────────────────────────────────────── */

export function upcomingHolidays(holidays = [], from = new Date(), limit = 5) {
  const t = startOfDay(from).getTime();
  return holidays
    .filter((h) => clean(h.HAH_IS_ACTIVE).toLowerCase() !== 'false')
    .map((h) => ({
      id: clean(h.HAH_HOLIDAY_ID),
      name: clean(h.HAH_HOLIDAY_NAME) || 'Holiday',
      type: clean(h.HAH_HOLIDAY_TYPE),
      date: h.HAH_HOLIDAY_DATE,
      _t: (parseDate(h.HAH_HOLIDAY_DATE) || new Date(0)).getTime(),
    }))
    .filter((h) => h._t >= t)
    .sort((a, b) => a._t - b._t)
    .slice(0, limit)
    .map((h) => ({
      ...h,
      daysAway: Math.round((h._t - t) / 86400000),
    }));
}

/* ── Overtime ────────────────────────────────────────────────────────────── */

const otApproved = (r) => {
  const raw = r.HOT_IS_APPROVED;
  if (raw === true) return true;
  const s = clean(raw).toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
};

export function overtimeKpis(rows = []) {
  const pending = rows.filter((r) => !otApproved(r));
  const approved = rows.filter(otApproved);
  const sumHours = (list) => list.reduce((s, r) => s + num(r.HOT_TOTAL_HOURS), 0);
  const sumAmount = (list) => list.reduce((s, r) => s + num(r.HOT_OT_AMOUNT), 0);

  return {
    pendingCount: pending.length,
    pendingHours: sumHours(pending),
    pendingAmount: sumAmount(pending),
    approvedHours: sumHours(approved),
    approvedAmount: sumAmount(approved),
    totalAmount: sumAmount(rows),
  };
}

/** OT hours and cost per month, oldest first. */
export function buildOvertimeTrend(rows = [], months = 6, today = new Date()) {
  const buckets = [];
  const index = {};
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
    const row = { key, label: `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`, hours: 0, amount: 0 };
    index[key] = row;
    buckets.push(row);
  }
  rows.forEach((r) => {
    const d = parseDate(r.HOT_OT_DATE);
    if (!d) return;
    const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
    if (!index[key]) return;
    index[key].hours += num(r.HOT_TOTAL_HOURS);
    index[key].amount += num(r.HOT_OT_AMOUNT);
  });
  return buckets.map((b) => ({
    ...b,
    hours: Math.round(b.hours * 10) / 10,
    amount: Math.round(b.amount),
  }));
}

/* ── Payroll ─────────────────────────────────────────────────────────────── */

/** Only the current salary rows count towards monthly cost. */
const currentSalary = (r) => {
  const cur = r.HES_IS_CURRENT;
  const act = r.HES_IS_ACTIVE;
  const truthy = (v) =>
    v === true || v === 1 || ['true', '1', 'yes'].includes(clean(v).toLowerCase());
  // Treat a missing flag as current — better to count a live salary than to
  // under-report the payroll bill.
  const isCurrent = cur === undefined || cur === null ? true : truthy(cur);
  const isActive = act === undefined || act === null ? true : truthy(act);
  return isCurrent && isActive;
};

export function payrollKpis(salaries = []) {
  const rows = salaries.filter(currentSalary);
  const gross = rows.reduce((s, r) => s + num(r.HES_GROSS_SALARY), 0);
  const basic = rows.reduce((s, r) => s + num(r.HES_BASIC_SALARY), 0);

  return {
    employeesOnPayroll: new Set(rows.map((r) => clean(r.HES_EMPLOYEE_ID)).filter(Boolean)).size,
    monthlyGross: gross,
    monthlyBasic: basic,
    allowances: Math.max(0, gross - basic),
    avgGross: rows.length ? gross / rows.length : 0,
  };
}

/** Basic vs everything else — the two halves of the monthly bill. */
export function buildSalaryComposition(kpis) {
  return [
    { name: 'Basic', value: Math.round(kpis.monthlyBasic), color: '#047857' },
    { name: 'Allowances', value: Math.round(kpis.allowances), color: '#0891b2' },
  ].filter((d) => d.value > 0);
}

/** Highest current gross salaries, for the cost-concentration list. */
export function buildTopSalaries(salaries = [], limit = 5) {
  return salaries
    .filter(currentSalary)
    .map((r) => ({
      id: clean(r.HES_EMPLOYEE_SALARY_ID),
      serviceNo: clean(r.HED_SERVICE_NO),
      name: clean(r.HED_REPORT_NAME) || clean(r.HED_SERVICE_NO) || '—',
      gross: num(r.HES_GROSS_SALARY),
      basic: num(r.HES_BASIC_SALARY),
    }))
    .sort((a, b) => b.gross - a.gross)
    .slice(0, limit);
}

/** The period the company is currently running, or the newest one. */
export function currentPeriod(periods = []) {
  if (!periods.length) return null;
  const open = periods.find((p) => clean(p.HPP_STATUS).toLowerCase() === 'open');
  const pick = open || [...periods].sort((a, b) => {
    const ad = parseDate(a.HPP_PAYROLL_START_DATE) || new Date(0);
    const bd = parseDate(b.HPP_PAYROLL_START_DATE) || new Date(0);
    return bd - ad;
  })[0];

  return {
    id: clean(pick.HPP_PAYROLL_PERIOD_ID),
    name: clean(pick.HPP_PERIOD_NAME) || clean(pick.HPP_PERIOD_CODE) || '—',
    status: clean(pick.HPP_STATUS) || 'Unknown',
    start: pick.HPP_PAYROLL_START_DATE,
    end: pick.HPP_PAYROLL_END_DATE,
    paymentDate: pick.HPP_PAYMENT_DATE,
    locked: clean(pick.HPP_IS_LOCKED).toLowerCase() === 'true',
    glPosted: clean(pick.HPP_GL_POSTED).toLowerCase() === 'true',
    bankGenerated: clean(pick.HPP_BANK_TRANSFER_GENERATED).toLowerCase() === 'true',
  };
}

/* ── Loans ───────────────────────────────────────────────────────────────── */

export function loanKpis(loans = []) {
  const live = loans.filter((l) => {
    const status = clean(l.HLM_STATUS).toLowerCase();
    return status !== 'closed' && status !== 'settled' && status !== 'rejected';
  });
  return {
    activeLoans: live.length,
    outstanding: live.reduce((s, l) => s + num(l.HLM_BALANCE_AMOUNT), 0),
    monthlyRecovery: live.reduce((s, l) => s + num(l.HLM_INSTALLMENT_AMOUNT), 0),
    borrowers: new Set(live.map((l) => clean(l.HLM_EMPLOYEE_ID)).filter(Boolean)).size,
  };
}

/* ── Action centre ───────────────────────────────────────────────────────── */

/**
 * Everything waiting on a human, in one list. Rows with a zero count are kept
 * out by the page so the panel only ever shows real work.
 */
export function buildActionItems({ headcount, pendingBanks, leave, ot }) {
  return [
    { key: 'employees', label: 'Employee approvals', count: headcount.pendingApproval, to: '/hrm/employees', tone: 'amber' },
    { key: 'banks',     label: 'Bank account approvals', count: pendingBanks,          to: '/hrm/employees', tone: 'amber' },
    { key: 'leave',     label: 'Leave requests',   count: leave.pending,               to: '/hrm/leave/approval', tone: 'blue' },
    { key: 'ot',        label: 'Overtime approvals', count: ot.pendingCount,           to: '/hrm/overtime/details', tone: 'violet' },
  ];
}